/**
 * Shared "Refreshed X ago" formatter for the campaign grid and perf grid.
 *
 * Both pages render this label off the same metrics_fetched_at column and used
 * to carry their own copy of the formatter (PerfGridPage's even said "mirrors
 * CampaignGridPage"). They had already drifted apart once — the grid reduced
 * rows with the oldest timestamp while perf used the newest — so the two live
 * here together and can't diverge again.
 *
 * Rolls minutes → hours → days → months → years, so a long-stale grid reads
 * "Refreshed 2mo ago" instead of "Refreshed 1358h ago".
 */

const MIN = 60 * 1000;
const HOUR = 60 * MIN;
const DAY = 24 * HOUR;
// Approximate, and deliberately so: this is a staleness hint, not a date
// calculation. A "month" of 30 days and a "year" of 365 keep the thresholds
// stable without dragging in calendar arithmetic.
const MONTH = 30 * DAY;
const YEAR = 365 * DAY;

/**
 * @param {number|null|undefined} ms epoch ms of the last refresh
 * @returns {string|null} e.g. "Refreshed 3m ago", or null when ms is absent
 *
 * Each unit floors rather than rounds, so the label never renders an impossible
 * boundary value ("60m ago", "24h ago") and never claims more staleness than has
 * actually elapsed. 90 minutes reads "1h ago", not "2h ago".
 */
export function formatRefreshedAgo(ms) {
  if (ms == null) return null;
  const elapsed = Math.max(0, Date.now() - ms);

  if (elapsed < MIN) return 'Refreshed just now';
  if (elapsed < HOUR) return `Refreshed ${Math.floor(elapsed / MIN)}m ago`;
  if (elapsed < DAY) return `Refreshed ${Math.floor(elapsed / HOUR)}h ago`;
  if (elapsed < MONTH) return `Refreshed ${Math.floor(elapsed / DAY)}d ago`;
  if (elapsed < YEAR) return `Refreshed ${Math.floor(elapsed / MONTH)}mo ago`;
  return `Refreshed ${Math.floor(elapsed / YEAR)}y ago`;
}

/**
 * Newest metrics_fetched_at across rows, in epoch ms — i.e. when the grid last
 * successfully pulled anything. Null when no row has cached metrics.
 *
 * Newest, not oldest: the rows a grid DISPLAYS are a superset of the rows the
 * cron REFRESHES (listGridEligibleCampaigns restricts by destination, status,
 * platform_campaign_id and a 90-day window; GET /campaign-grid does not), so a
 * campaign that falls out of the eligible set keeps its last-ever fetched_at
 * forever and would pin the minimum to months stale. The maximum still climbs
 * correctly when the pull genuinely breaks, because then nothing is written and
 * every row ages together.
 *
 * @param {Array<{metrics_fetched_at?: string|null}>} rows
 * @returns {number|null}
 */
export function newestMetricsFetchedAt(rows) {
  let newest = null;
  for (const c of rows || []) {
    if (!c || !c.metrics_fetched_at) continue;
    const t = new Date(c.metrics_fetched_at).getTime();
    if (Number.isNaN(t)) continue;
    if (newest === null || t > newest) newest = t;
  }
  return newest;
}
